import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { GlobalService } from 'app/global/global.service';
import { CustomerOnboardFlowService } from 'app/main/pages/customer-onboard-flow/customer-onboard-flow.service';
import { OnboardingFacade } from 'app/main/pages/customer-onboard-flow/store/onboarding/onboarding.facade';
import { selectUserProfileSubmitted } from 'app/main/pages/customer-onboard-flow/store/onboarding/onboarding.selectors';
import Stepper from 'bs-stepper';
import { environment } from 'environments/environment';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { CoreConfig } from '../../../../../@core/types';
import { ConfirmInvitationFacadeService } from '../../store/confirm-invitation-facade.service';
import { IErrorResponse } from '../../store/confirm-invitation.reducers';
import { ResponseCodeEnum } from '../../store/response-code.enum';

@Component({
    selector: 'app-confirm-invitation',
    templateUrl: './confirm-invitation.component.html',
    styleUrl: './confirm-invitation.component.scss'
})
export class ConfirmInvitationComponent implements OnInit, OnDestroy {

    userProfileSubmitted$ = this.store.select(selectUserProfileSubmitted);

    // Public
    public invitedOnboardingForm: FormGroup;
    public privacyPolicyTerms: string = environment.privacyTerms;
    public coreConfig: CoreConfig;
    public inviteFormDisabled: boolean = false;
    public confirmInvitationUrlError$: Observable<IErrorResponse | null>

    // Private
    private _unsubscribeAll: Subject<void> = new Subject<void>();
    private apiUrl: string;
    private horizontalWizardStepper: Stepper;
    private currentStepIndex = 0;
    private _stepperEl: ElementRef;
    @ViewChild('stepper') set stepperEl(el: ElementRef) {
        if (el && !this.horizontalWizardStepper) {
            this._stepperEl = el;
            this.horizontalWizardStepper = new Stepper(this._stepperEl.nativeElement, {
                linear: false
            });
            this.horizontalWizardStepper.to(0);
            this.currentStepIndex = 0;
        }
    }

    constructor(
        private formBuilder: FormBuilder,
        private customerOnboardFlowService: CustomerOnboardFlowService,
        private _globalService$: GlobalService,
        private route: ActivatedRoute,
        private _confirmInvitationFacadeService: ConfirmInvitationFacadeService,
        private onboardingFacade: OnboardingFacade,
        private store: Store,
        private translocoService: TranslocoService
    ) {
        this._unsubscribeAll = new Subject();
    }

    horizontalWizardStepperNext() {
        if (this.currentStepIndex === 2) {
            return;
        }
        this.horizontalWizardStepper.next();
        this.currentStepIndex++;
    }

    horizontalWizardStepperPrevious() {
        if (this.currentStepIndex === 0) {
            return;
        }
        const userProfileFormGroup = this.invitedOnboardingForm.get('userProfile');
        if (userProfileFormGroup?.disabled && this.currentStepIndex === 2) {
            this.horizontalWizardStepper.previous();
            this.currentStepIndex--;
        }
        this.horizontalWizardStepper.previous();
        this.currentStepIndex--;
    }

    get f() {
        return this.invitedOnboardingForm.controls;
    }

    ngOnInit(): void {
        this.onboardingFacade.setOnboardingType('invitation');
        this.apiUrl = this.route.snapshot.queryParamMap.get('url') || '';
        this.invitedOnboardingForm = this.customerOnboardFlowService.buildOnboardingForm(this.formBuilder, true);

        this._confirmInvitationFacadeService.confirmInvitationUrlError$.pipe(
            filter((error: IErrorResponse | null) => error !== null && error?.status === 419),
            takeUntil(this._unsubscribeAll)
        ).subscribe(() => {
            this.invitedOnboardingForm.disable();
            this.inviteFormDisabled = true;
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        this._confirmInvitationFacadeService.clearState();
        this.onboardingFacade.resetState();
    }

    onBoardingSubmit() {
        this.onboardingFacade.updateError(null);
        this.onboardingFacade.isValid$.pipe(take(1)).subscribe(isValid => {
            if (isValid) {
                this.onboardingFacade.onboardingFormData$.pipe(take(1)).subscribe(finalFormData => {
                    this.onboardingFacade.submitInvitedOnboarding(finalFormData, this.apiUrl);
                });
            } else {
                this.markAllAsTouched(this.invitedOnboardingForm);
                const errorMsg = this.translocoService.translate('invalid form error message(Onboarding form) - Form is invalid, please check the fields and try again.');
                this.onboardingFacade.updateError(errorMsg);
            }
        });
    }

    private markAllAsTouched(formGroup: FormGroup): void {
        Object.values(formGroup.controls).forEach(control => {
            if (control instanceof FormGroup) {
                this.markAllAsTouched(control);
            } else {
                control.markAsTouched();
            }
        });
    }

    protected readonly ResponseCodeEnum = ResponseCodeEnum;
}